import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import SearchableSelect from '@/components/SearchableSelect';
import { trPara } from '@/lib/sayi';
import { Loader2, Sparkles, Search, Paperclip, CheckCircle2, ZoomIn, X, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';

const PB = ['TRY', 'USD', 'EUR', 'GBP'];
const gun = (t) => (t ? t.split('-').reverse().join('.') : '');

/**
 * TAHSİLAT MAKBUZUNU ÖDEME HAREKETİNE EKLER (kullanıcı, 2026-10-03).
 *
 * Makbuz yeni bir kayıt doğurmaz; zaten girilmiş bir ödemenin karşı taraftan
 * gelen belgesidir. Pencere: belge kilitlenir → AI okur (firma, tarih, tutar)
 * → cari eşleşir → o tarihe yakın ödemeler aday olarak sıralanır → kullanıcı
 * SEÇER → sayfalar hareketin eklerine kopyalanır, belge "işlendi" olur.
 * Otomatik bağlama YOK: aynı gün aynı tutarda iki ödeme olabilir.
 * Vazgeçilirse kilit bırakılır.
 *
 * MAKBUZ GÖRSELİ PENCERENİN İÇİNDE (kullanıcı, 2026-10-03: "teyit etmek için
 * makbuz görselini görebilmem lazım ama bu ekranda göremiyorum; kapatırsam
 * işlem başa saracak"). Solda sayfa, tıklayınca pencere İÇİNDE büyür (ayrı bir
 * pencere açmak bu pencereyi kapatırdı). Okuma sonucu sunucuda belgeye
 * saklanır; yeniden açınca model tekrar çağrılmaz, "Yeniden oku" ister.
 */
export default function MakbuzIsle({ belge, onKapat, onIslendi }) {
  const [okunuyor, setOkunuyor] = useState(false);
  const [araniyor, setAraniyor] = useState(false);
  const [ekleniyor, setEkleniyor] = useState(false);
  const [cariler, setCariler] = useState([]);
  const [alan, setAlan] = useState({ cari_id: '', tarih: '', tutar: '', para_birimi: 'TRY', makbuz_no: '' });
  const [okunan, setOkunan] = useState(null);
  const [adaylar, setAdaylar] = useState([]);
  const [secili, setSecili] = useState(null);
  // Makbuz sayfası (JWT gerektiği için blob → object URL)
  const sayfalar = (belge?.sayfalar || []).slice().sort((a, b) => (a.sira || 0) - (b.sira || 0));
  const [sayfa, setSayfa] = useState(1);
  const [gorselUrl, setGorselUrl] = useState(null);
  const [buyuk, setBuyuk] = useState(false);
  const aktifSayfa = sayfalar.find(x => x.sira === sayfa);
  const pdf = (aktifSayfa?.content_type || '').includes('pdf');

  useEffect(() => {
    if (!belge || !sayfalar.length) return;
    let iptal = false, olusan = null;
    setGorselUrl(null);
    api.get(`/dosya-kutusu/${belge.id}/sayfa/${sayfa}`, { responseType: 'blob' })
      .then(r => { if (!iptal) { olusan = URL.createObjectURL(r.data); setGorselUrl(olusan); } })
      .catch(() => {});
    return () => { iptal = true; if (olusan) URL.revokeObjectURL(olusan); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [belge?.id, sayfa]);

  const okumayiUygula = (veri, iptal) => {
    if (iptal) return;
    const o = veri.okunan || {};
    setOkunan({ ...o, cari_adi: veri.cari_adi });
    setAlan({
      cari_id: veri.cari_id || '', tarih: o.tarih || '',
      tutar: o.tutar != null ? String(o.tutar) : '', para_birimi: o.para_birimi || 'TRY',
      makbuz_no: o.makbuz_no || '',
    });
    setAdaylar(veri.adaylar || []);
    setSecili(null);
    const tutanlar = (veri.adaylar || []).filter(a => a.tutar_tuttu);
    // Tutarı birebir tutan TEK aday varsa önden işaretlenir; seçim yine kullanıcınındır
    if (tutanlar.length === 1) setSecili(tutanlar[0].id);
    else if (!(veri.adaylar || []).length) toast.info('Uygun ödeme bulunamadı — alanları düzeltip yeniden arayın');
  };

  const yenidenOku = async () => {
    setOkunuyor(true);
    try {
      const r = await api.post(`/makbuz/${belge.id}/oku`, null, { params: { yeniden: true } });
      okumayiUygula(r.data, false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Makbuz okunamadı');
    } finally {
      setOkunuyor(false);
    }
  };

  useEffect(() => {
    if (!belge) return;
    let iptal = false;
    setOkunan(null); setAdaylar([]); setSecili(null); setSayfa(1);
    api.get('/makbuz/cariler').then(r => { if (!iptal) setCariler(r.data || []); }).catch(() => {});
    (async () => {
      setOkunuyor(true);
      try {
        await api.post(`/dosya-kutusu/${belge.id}/kilitle`);
        const r = await api.post(`/makbuz/${belge.id}/oku`);
        if (iptal) return;
        okumayiUygula(r.data, iptal);
      } catch (err) {
        if (!iptal) toast.error(err.response?.data?.detail || 'Makbuz okunamadı — alanları elle doldurun');
      } finally {
        if (!iptal) setOkunuyor(false);
      }
    })();
    return () => { iptal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [belge]);

  const ara = useCallback(async () => {
    setAraniyor(true); setSecili(null);
    try {
      const r = await api.get('/makbuz/adaylar', { params: {
        cari_id: alan.cari_id || undefined, tarih: alan.tarih || undefined,
        tutar: alan.tutar ? parseFloat(String(alan.tutar).replace(',', '.')) : undefined,
        para_birimi: alan.para_birimi || undefined } });
      setAdaylar(r.data || []);
      if (!(r.data || []).length) toast.info('Bu ölçülerle ödeme bulunamadı');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Arama yapılamadı');
    } finally {
      setAraniyor(false);
    }
  }, [alan]);

  const vazgec = async () => {
    try { await api.post(`/dosya-kutusu/${belge.id}/birak`); } catch { /* yoksay */ }
    onKapat();
  };

  const ekle = async () => {
    if (!secili) return;
    setEkleniyor(true);
    try {
      const r = await api.post(`/makbuz/${belge.id}/ekle`, { hareket_id: secili, makbuz_no: alan.makbuz_no });
      toast.success(`Makbuz ödemeye eklendi — ${r.data.etiket}`);
      onIslendi();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    } finally {
      setEkleniyor(false);
    }
  };

  return (
    <Dialog open={Boolean(belge)} onOpenChange={(a) => { if (!a) vazgec(); }}>
      <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-5xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="makbuz-isle">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916] font-bold flex items-center gap-2">
            <Paperclip size={18} /> Makbuzu Ödemeye Ekle
          </DialogTitle>
          <DialogDescription className="text-[#3F3E39] text-sm">
            Makbuz yeni bir kayıt açmaz; seçtiğiniz ödeme hareketinin eklerine (ataş) belge olarak eklenir.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 items-start">
        {/* ── Makbuz görseli: teyit için pencerenin İÇİNDE ── */}
        <div className="md:sticky md:top-0 space-y-1.5" data-testid="makbuz-gorsel">
          <div className="relative rounded-lg border border-[#d0c4b0] bg-white overflow-hidden min-h-[160px] flex items-center justify-center">
            {!gorselUrl ? (
              <Loader2 className="animate-spin text-[#8a7e70]" size={20} />
            ) : pdf ? (
              <iframe src={gorselUrl} title="Makbuz" className="w-full h-[55vh]" />
            ) : (
              <button type="button" onClick={() => setBuyuk(true)} className="block w-full cursor-zoom-in group"
                title="Büyütmek için tıklayın">
                <img src={gorselUrl} alt="Makbuz" className="w-full max-h-[55vh] object-contain" />
                <span className="absolute bottom-1.5 right-1.5 bg-black/55 text-white rounded px-1.5 py-0.5 text-[10px] flex items-center gap-1 opacity-80 group-hover:opacity-100">
                  <ZoomIn size={11} /> Büyüt
                </span>
              </button>
            )}
          </div>
          {sayfalar.length > 1 && (
            <div className="flex items-center justify-center gap-2 text-xs text-[#6b5f4f]">
              <button type="button" disabled={sayfa <= 1} onClick={() => setSayfa(x => x - 1)} className="disabled:opacity-30"><ChevronLeft size={16} /></button>
              {sayfa} / {sayfalar.length}
              <button type="button" disabled={sayfa >= sayfalar.length} onClick={() => setSayfa(x => x + 1)} className="disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          )}
        </div>

        <div className="space-y-3 min-w-0">
        {okunuyor ? (
          <div className="py-10 flex flex-col items-center gap-2 text-[#8a7e70] text-sm">
            <Sparkles size={22} className="animate-pulse text-[#3d6e4e]" /> Makbuz okunuyor…
          </div>
        ) : (
          <>
            {okunan && (
              <p className="text-xs text-[#8a7e70] -mt-1" data-testid="makbuz-okunan">
                AI okudu: <b className="text-[#1a1916]">{okunan.firma_adi || '—'}</b>
                {okunan.tarih ? ` · ${gun(okunan.tarih)}` : ''}
                {okunan.tutar != null ? ` · ${trPara(okunan.tutar)} ${okunan.para_birimi}` : ''}
                {okunan.makbuz_no ? ` · No ${okunan.makbuz_no}` : ''}
                {okunan.firma_adi && !okunan.cari_adi && <span className="text-[#8a5c00]"> — cari eşleşmedi, listeden seçin</span>}
                <button type="button" onClick={yenidenOku}
                  className="ml-2 inline-flex items-center gap-0.5 text-[#3d6e4e] hover:underline" data-testid="makbuz-yeniden-oku">
                  <RotateCcw size={11} /> Yeniden oku
                </button>
              </p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-12 gap-2">
              <div className="col-span-2 md:col-span-5 min-w-0">
                <p className="text-[10px] font-semibold uppercase text-[#8a7e70] mb-1">Cari</p>
                <SearchableSelect value={alan.cari_id}
                  options={cariler.map(c => ({ value: c.id, label: c.name, hint: c.account_type }))}
                  onChange={(v) => setAlan(p => ({ ...p, cari_id: v }))}
                  placeholder="Cari seçin" searchPlaceholder="Cari ara..." testId="makbuz-cari" />
              </div>
              <div className="col-span-1 md:col-span-3">
                <p className="text-[10px] font-semibold uppercase text-[#8a7e70] mb-1">Makbuz Tarihi</p>
                <Input type="date" value={alan.tarih} onChange={(e) => setAlan(p => ({ ...p, tarih: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="makbuz-tarih" />
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-[10px] font-semibold uppercase text-[#8a7e70] mb-1">Tutar</p>
                <Input inputMode="decimal" value={alan.tutar} onChange={(e) => setAlan(p => ({ ...p, tutar: e.target.value }))}
                  className="bg-white border-[#d0c4b0] text-right font-mono" data-testid="makbuz-tutar" />
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-[10px] font-semibold uppercase text-[#8a7e70] mb-1">PB</p>
                <select value={alan.para_birimi} onChange={(e) => setAlan(p => ({ ...p, para_birimi: e.target.value }))}
                  className="w-full h-10 rounded-md border border-[#d0c4b0] bg-white px-2 text-sm">
                  {PB.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="col-span-1 md:col-span-12 flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={ara} disabled={araniyor}
                  className="border-[#d0c4b0]" data-testid="makbuz-ara">
                  {araniyor ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />}
                  Ödemeleri ara
                </Button>
              </div>
            </div>

            <div className="rounded-md border border-[#e6dcc9] bg-white divide-y divide-[#f0e9dc]" data-testid="makbuz-adaylar">
              {adaylar.length === 0 && (
                <p className="p-4 text-center text-sm text-[#8a7e70]">
                  Aday ödeme yok. Cari, tarih ya da tutarı düzeltip "Ödemeleri ara"ya basın.
                </p>
              )}
              {adaylar.map(a => (
                <label key={a.id} className={`flex items-start gap-3 px-3 py-2.5 cursor-pointer text-sm
                  ${secili === a.id ? 'bg-[#3d6e4e]/10' : 'hover:bg-[#f5f1e8]'}`}
                  data-testid={`makbuz-aday-${a.id}`}>
                  <input type="radio" name="aday" checked={secili === a.id} onChange={() => setSecili(a.id)}
                    className="mt-1 accent-[#3d6e4e]" />
                  <span className="flex-1 min-w-0">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-mono tabular-nums font-semibold text-[#1a1916]">
                        {trPara(a.tutar)} {a.para_birimi}
                      </span>
                      <span className="text-[#3F3E39]">{gun(a.tarih)}</span>
                      {a.tutar_tuttu && <span className="text-[10px] font-semibold text-[#3d6e4e] bg-[#3d6e4e]/10 rounded px-1.5">tutar tutuyor</span>}
                      {a.tutar_benzer && <span className="text-[10px] font-semibold text-[#8a5c00] bg-[#8a5c00]/10 rounded px-1.5"
                        title="Okunan tutardan tek rakam farklı — el yazısı yanlış okunmuş olabilir">benzer tutar</span>}
                      {a.gun_farki != null && a.gun_farki > 0 && <span className="text-[10px] text-[#8a7e70]">{a.gun_farki} gün fark</span>}
                      {a.ek_sayisi > 0 && <span className="text-[10px] text-[#8a7e70] inline-flex items-center gap-0.5"><Paperclip size={10} />{a.ek_sayisi} ek var</span>}
                    </span>
                    <span className="block text-xs text-[#6b5f4f] truncate max-w-full">
                      {a.cikan || '—'} → {a.giren || '—'}{a.aciklama ? ` · ${a.aciklama}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </>
        )}
        </div>
        </div>

        {/* Büyütülmüş görsel — AYRI pencere değil, bu pencerenin içinde katman */}
        {buyuk && gorselUrl && !pdf && (
          <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
            onClick={() => setBuyuk(false)} data-testid="makbuz-buyuk">
            <button type="button" className="absolute top-4 right-4 text-white" onClick={() => setBuyuk(false)}><X size={28} /></button>
            <img src={gorselUrl} alt="Makbuz" className="max-h-[92vh] max-w-[94vw] object-contain"
              onClick={(e) => e.stopPropagation()} />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={vazgec} className="border-[#d0c4b0]" data-testid="makbuz-vazgec">Vazgeç</Button>
          <Button onClick={ekle} disabled={!secili || ekleniyor || okunuyor}
            className="bg-[#3d6e4e] text-white hover:bg-[#2d5e3e] font-semibold" data-testid="makbuz-ekle">
            {ekleniyor ? <Loader2 size={14} className="mr-1 animate-spin" /> : <CheckCircle2 size={14} className="mr-1" />}
            Seçili ödemeye ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
